import { DEFAULT_AGENCY_SLUG } from '@saaga/shared/constants/agency.constants'
import { useCallback } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { agencyRoutes, miscRoutes, orgRoutes, settingsRoutes } from '@/routes/org-routes.const'

import { useCloseSplitViewOnRouteChange } from './use-close-split-view-on-route-change.hook'

export interface AppLayoutNavigationHandlers {
  handleCreateProject: () => void
  handleAgenciesClick: () => void
  handleAgenciesSettings: () => void
  handleCreateChat: () => void
  handleConnectsClick: () => void
  handleOrgSettings: () => void
  isPathActive: (path: string) => boolean
}

export const useAppLayoutNavigation = (): AppLayoutNavigationHandlers => {
  const location = useLocation()
  const navigate = useNavigate()
  const { orgSlug } = useParams<{ orgSlug: string }>()
  useCloseSplitViewOnRouteChange()

  const isPathActive = useCallback(
    (path: string): boolean => {
      if (!orgSlug) return false

      const fullPath = path === '/' ? `/${orgSlug}` : `/${orgSlug}${path}`

      if (path === '/') {
        return location.pathname === fullPath
      }

      const pathSegmentList = fullPath.split('/').filter(Boolean)
      const locationSegmentList = location.pathname.split('/').filter(Boolean)

      return pathSegmentList.every((segment, index) => locationSegmentList[index] === segment)
    },
    [location.pathname, orgSlug]
  )

  const handleCreateProject = useCallback(() => {
    navigate(`/${orgSlug}/${miscRoutes.projects}/${agencyRoutes.new}`)
  }, [navigate, orgSlug])

  const handleAgenciesClick = useCallback(() => {
    navigate(`/${orgSlug}/${orgRoutes.agencies}/${agencyRoutes.explore}`)
  }, [navigate, orgSlug])

  const handleAgenciesSettings = useCallback(() => {
    navigate(`/${orgSlug}/${orgRoutes.agencies}/${agencyRoutes.studio}`)
  }, [navigate, orgSlug])

  const handleCreateChat = useCallback(() => {
    navigate(`/${orgSlug}/${orgRoutes.chat}/${DEFAULT_AGENCY_SLUG}`)
  }, [navigate, orgSlug])

  const handleConnectsClick = useCallback(() => {
    navigate(`/${orgSlug}/${orgRoutes.settings}/${settingsRoutes.connectors}`)
  }, [navigate, orgSlug])

  const handleOrgSettings = useCallback(() => {
    navigate(`/${orgSlug}/${orgRoutes.settings}`)
  }, [navigate, orgSlug])

  return {
    handleCreateProject,
    handleAgenciesClick,
    handleAgenciesSettings,
    handleCreateChat,
    handleConnectsClick,
    handleOrgSettings,
    isPathActive,
  }
}
